import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { api, type TestPool } from '../api/client'
import { fmtInt, fmtRelative } from '../lib/format'

function describeError(err: unknown): string | null {
  if (!err) return null
  if (err instanceof Error) return err.message
  return String(err)
}

export function TestPoolsPage() {
  const queryClient = useQueryClient()
  const list = useQuery({
    queryKey: ['test-pools'],
    queryFn: () => api.get<{ items: TestPool[] }>('/api/test-pools'),
  })

  const [open, setOpen] = useState(false)
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api.post<TestPool>('/api/test-pools', {
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-pools'] })
      setOpen(false)
      setSlug('')
      setName('')
      setDescription('')
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.del<{ ok: boolean }>(`/api/test-pools/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['test-pools'] }),
  })

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3">
        <Link to="/subscribers" className="label-amber hover:underline">← Subscribers</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Test Pools</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Subscribers · pre-vetted lists</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Test Pools
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">campaign QA recipients</span>
          </h1>
          <p className="text-paper-dim mt-2 text-[13px] max-w-[700px]">
            Named lists of subscribers used for live test sends from the campaign
            editor. Pools are not filtered by segment — they're just "people who
            agreed to receive test emails."
          </p>
        </div>
        <Button size="lg" onClick={() => setOpen(true)}>+ New Pool</Button>
      </header>

      {list.isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
      {list.data && list.data.items.length === 0 && (
        <div className="label text-paper-trace p-6">
          No test pools yet — create your first with <span className="label-amber">+ New Pool</span>.
        </div>
      )}

      {list.data && list.data.items.length > 0 && (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Pool</th>
                <th className="label py-2 px-3 font-normal text-left">Description</th>
                <th className="label py-2 px-3 font-normal text-right">Members</th>
                <th className="label py-2 px-3 font-normal text-left">Created</th>
                <th className="label py-2 px-3 font-normal text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr key={p.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <td className="px-4 py-2">
                    <Link to={`/subscribers/test-pools/${p.id}`} className="text-paper hover:text-amber-glow">
                      {p.name}
                    </Link>
                    <div className="text-[10px] text-paper-trace">{p.slug}</div>
                  </td>
                  <td className="px-3 py-2 text-paper-dim">{p.description || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-paper">{fmtInt(p.member_count)}</td>
                  <td className="px-3 py-2 text-paper-dim">{p.created_at ? fmtRelative(p.created_at) : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete pool "${p.name}"? Members will not be deleted; only the pool is removed.`)) {
                          remove.mutate(p.id)
                        }
                      }}
                      className="label text-radar-red hover:underline disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={() => setOpen(false)}>
          <div className="bg-ink-700 border border-ink-500 p-6 w-[480px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-[24px] text-paper">New test pool</h2>
            <div className="space-y-3">
              <div>
                <div className="label mb-1">Slug</div>
                <input
                  className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[13px]"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="e.g. ops-internal"
                />
              </div>
              <div>
                <div className="label mb-1">Name</div>
                <input
                  className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Ops Internal"
                />
              </div>
              <div>
                <div className="label mb-1">Description (optional)</div>
                <textarea
                  className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px] resize-none h-20"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this pool for?"
                />
              </div>
            </div>
            {describeError(create.error) && (
              <div className="font-mono text-[12px] text-radar-red">{describeError(create.error)}</div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="md" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                size="md"
                disabled={create.isPending || !slug.trim() || !name.trim()}
                onClick={() => create.mutate()}
              >
                {create.isPending ? 'Creating…' : 'Create pool'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
